import React from 'react';
import SimpleCenteredHeaderSection from '@/components/HeaderSection/SimpleCenteredHeaderSection';
import CenteredContentSection from '@/components/ContentSection/CenteredContentSection';
import { H3, H4, H5 } from '@/components/Element/Heading';
import RollingLeadForm from '@/components/LeadForm/RollingLeadForm';
import Link from 'next/link';
import BatteryHero from '@/images/solar-customer-resources/ev-charge.jpg';
import FullWidthBarBreadcrumbs from '@/components/Breadcrumb/FullWidthBarBreadcrumbs';
import { FaBolt, FaCarBattery } from 'react-icons/fa';
import { BsSpeedometer } from 'react-icons/bs';
import ShadeSelector from 'components/EVCalculator/ShadeSelect';
import PanelCard from 'components/EVCalculator/PanelCard';
import { getAllVehicles } from 'data/database';
import { Vehicle } from 'data/types';
import { useState } from 'react';
import Image from 'next/legacy/image';
import MetaTags from '@/components/Head/MetaTags';
import { useRouter } from 'next/router';

export async function getStaticProps() {
  const vehicles = await getAllVehicles();
  console.log(vehicles);
  return { props: { vehicles } };
}

type Props = {
  vehicles: Array<Vehicle>;
};

const Calculator = ({ vehicles }: Props) => {
  // const [vehicleSlugs, setVehicleSlugs] = useState<Vehicle[]>([]);
  const router = useRouter();
  const cid = Array.isArray(router.query.cid)
    ? router.query.cid[0]
    : router.query.cid || '';

  const [selectedSlug, setSelectedSlug] = useState<string>('');
  const [selectedImgPath, setSelectedImgPath] = useState<string>(
    'https://cdn-energypal-com.s3.amazonaws.com/vehicles/chevrolet_spark_ev_old.jpg'
  );
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedName, setSelectedName] = useState<string>('');
  const [selectedTrim, setSelectedTrim] = useState<string>('');
  const [selectedValue, setSelectedValue] = useState<string>('10000');
  const [efficiencyFirstPart, setEfficiencyFirstPart] = useState('0 kWH');
  const [efficiencySecondPart, setEfficiencySecondPart] = useState('0 miles');
  const [batteryPackCapacity, setBatteryPackCapacity] = useState('0 kWh');
  const [selectedVehicle, setSelectedVehicle] = useState(false);
  const [selectedShade, setSelectedShade] = useState(0);

  let efficiencyRatio = '';

  const handleSlugChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSlug(event.target.value.split('~')[0]);
    setBatteryPackCapacity(event.target.value.split('~')[1]);
    setSelectedMake(event.target.value.split('~')[6]);
    // setImgPath(event.target.value.split('~')[3]);
    setSelectedName(event.target.value.split('~')[4]);
    setSelectedTrim(event.target.value.split('~')[5]);
    setEfficiencyFirstPart(event.target.value.split('~')[2].split('/')[0]);
    setEfficiencySecondPart(event.target.value.split('~')[2].split('/')[1]);
    setSelectedVehicle(true);
    setSelectedImgPath(
      'https://cdn-energypal-com.s3.amazonaws.com/vehicles/' +
        event.target.value.split('~')[0] +
        '.jpg'
    );
  };

  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedValue(event.target.value);
  };

  const handleShadeChange = (shade: number) => {
    setSelectedShade(shade);
  };

  if (selectedVehicle) {
    efficiencyRatio = (
      parseInt(efficiencySecondPart) / parseInt(efficiencyFirstPart)
    ).toFixed(2);
  }

  const kwNeeded =
    ((parseInt(selectedValue) * 0.8) /
      parseFloat(efficiencyRatio) /
      (0.16 * (1 + selectedShade / 100)) /
      365 /
      24) *
    1000;

  const equivalentCost = Math.round((parseInt(selectedValue) / 27) * 5);
  const panelsNeeded400 = Math.round(kwNeeded / 400).toString();
  const panelsNeeded365 = Math.round(kwNeeded / 365).toString();
  const panelsNeeded440 = Math.round(kwNeeded / 440).toString();

  return (
    <>
      <MetaTags
        title={`Solar EV Charging Calculator`}
        description={`Discover the savings and environmental benefits of solar EV charging with our solar EV charging calculator. Explore adding solar into your EV charging setup.`}
      />
      <FullWidthBarBreadcrumbs
        pages={[
          {
            name: 'Resources',
            href: '/solar-customer-resources',
          },
          {
            name: 'Best EVs',
            href: '/best-electric-vehicles',
          },
          {
            name: 'Calculator',
            href: `/best-electric-vehicles/calculator`,
          },
        ]}
      />
      <SimpleCenteredHeaderSection
        dark
        image={BatteryHero}
        supTitle={`Electric Vehicle Guide`}
        title={'Solar EV Charging Calculator'}
        subTitle="Discover the savings and environmental benefits of solar EV charging with our solar EV charging calculator. Explore adding solar into your EV charging setup."
      />

      <div>
        <div className="flex lg:flex-row flex-col mt-4">
          <div className="bg-slate-100 rounded-t-md md:rounded-l-md  lg:w-1/2 mx-4 md:m-4 md:mr-0 p-2 pt-8">
            <div className="px-4 md:px-12 pb-4">
              <div className="mb-4 space-y-2">
                <H4>What vehicle do you have?</H4>
                <select
                  className="border border-gray-300 rounded-md w-full"
                  value={selectedSlug}
                  onChange={handleSlugChange}
                >
                  <option value="">Select Vehicle</option>
                  {vehicles
                    .slice()
                    .sort((a, b) => {
                      const nameA = a.name?.toUpperCase() || '';
                      const nameB = b.name?.toUpperCase() || '';
                      return nameA.localeCompare(nameB);
                    })
                    .map((vehicle) => (
                      <option
                        key={vehicle.slug}
                        value={`${vehicle.slug}~${vehicle.batteryCapacity}~${vehicle.efficiencyKwH}~${vehicle.imgWebpPath}~${vehicle.name}~${vehicle.trim}~${vehicle.make}`}
                      >
                        {vehicle.name} {vehicle.trim}
                      </option>
                    ))}
                </select>
              </div>
              {selectedVehicle && (
                <div className="mb-4 ">
                  <div className="grid grid-cols-1 md:grid-cols-3">
                    <div className="md:col-span-3 mx-2 font-medium mb-2 text-xl mt-4 bg-slate-200 p-2 rounded-md inline-block whitespace-nowrap flex">
                      <div className="w-48 h-24 relative">
                        <Image
                          src={selectedImgPath}
                          layout="fill"
                          objectFit="cover"
                          className="rounded-sm shadow-sm"
                          alt={selectedName}
                        />
                      </div>
                      <div className=" ml-4">
                        <H3 className="text-primary-400 text-left">
                          {selectedName}
                        </H3>
                        <H4 className="text-black text-left">{selectedTrim}</H4>
                      </div>
                    </div>
                    <div className="text-center text-primary-400 font-medium mb-2 text-xl mt-4 bg-slate-200 p-2 rounded-md inline-block mx-2">
                      <FaBolt className="inline-block mr-1 mb-1 text-primary-500" />
                      {efficiencyFirstPart} / {efficiencySecondPart}
                      <p className="text-black text-base text-center">
                        Vehicle Efficiency
                      </p>
                    </div>
                    <div className="text-center text-primary-400 mx-2 font-medium mb-2 text-xl mt-4 bg-slate-200 p-2 rounded-md inline-block ">
                      <FaCarBattery className=" inline-block mr-1 mb-1 text-primary-500" />
                      {batteryPackCapacity}
                      <p className="text-black text-base text-center">
                        Battery Capacity
                      </p>
                    </div>
                    <Link
                      href={`/best-electric-vehicles/${selectedMake}/${selectedSlug}`}
                      legacyBehavior
                    >
                      <div className=" cursor-pointer text-center text-primary-400 mx-2 font-medium mb-2 text-xl mt-4 bg-slate-200 p-2 rounded-md inline-block ">
                        Want More Info?
                        <p className="text-black text-base text-center">
                          See Vehicle Guide →
                        </p>
                      </div>
                    </Link>
                  </div>
                </div>
              )}

              <H4 className="my-4">Yearly driving distance</H4>
              <input
                type="range"
                className="w-full h-3  rounded-md appearance-none cursor-pointer"
                min={5000}
                max={25000}
                step={2500}
                defaultValue={10000}
                value={selectedValue}
                onChange={handleValueChange}
              />
              <div className="flex justify-between">
                <h3 className="text-sm text-gray-500">5000</h3>
                <h3 className="text-sm text-gray-500">25000</h3>
              </div>
              <p className="text-primary-400 font-medium mb-2 text-xl mt-4 bg-slate-200 p-2 rounded-md inline-block whitespace-nowrap">
                <BsSpeedometer className="inline-block mr-2 mb-1 text-primary-500" />{' '}
                {selectedValue}{' '}
                <span className="text-black">Miles per year</span>
              </p>

              <H4 className="my-4">How shaded is your home&apos;s roof? </H4>
              <ShadeSelector onShadeChange={handleShadeChange} />
            </div>
          </div>
          <div className="bg-primary-500 rounded-b-md md:rounded-r-md lg:w-1/2 mx-4 md:m-4 p-2 pt-8 md:ml-0">
            <div className="px-4 md:px-12 pb-4">
              <H3 className="text-white pb-4">Results</H3>
              {selectedVehicle && (
                <H4 className="text-white py-3">
                  Your electricity usage will increase by{' '}
                  <strong>{Math.round(kwNeeded)} Kwh/Year.</strong>
                </H4>
              )}
              {selectedVehicle && (
                <H5 className="text-white py-3">
                  Save up to{' '}
                  <strong>${Math.round(equivalentCost)} per year</strong> by
                  going electric.
                </H5>
              )}
              {selectedVehicle && (
                <H4 className="text-white py-3">You need this many panels:</H4>
              )}
              {selectedVehicle && (
                <PanelCard
                  panel365={panelsNeeded365}
                  panel400={panelsNeeded400}
                  panel440={panelsNeeded440}
                />
              )}
              {!selectedVehicle && (
                <H4 className="text-white py-3">
                  Select a vehicle to see how many solar panels you need to
                  power it.
                </H4>
              )}
              {selectedVehicle && (
                <p className="text-white py-3 italic">
                  *Estimated savings assume a fuel efficiency of 27 MPG for a
                  gas-powered vehicle with an estimated fuel cost of $5 per
                  gallon.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* article */}
      <CenteredContentSection>
        <H3>Solar and EV Charging</H3>
        <p>
          The number of solar panels required to charge an EV varies. Factors
          such as the vehicle&apos;s energy consumption, charging frequency, and
          available sunlight need to be taken into account.
        </p>
        <H4>Energy Consumption:</H4>
        <p>
          If you are considering solar EV charging. Understanding the energy
          consumption of your electric vehicle is crucial. The energy
          consumption of an EV is{' '}
          <strong>
            typically measured in kilowatt-hours (kWh) per mile or kilometer.
          </strong>{' '}
          Different EV models have varying energy efficiencies. So, it&apos;s
          important to research and{' '}
          <strong>
            understand your specific vehicle&apos;s energy consumption rate
          </strong>
          . Your driving style also plays a role. As an EV driver you understand
          the rush of acceleration firsthand.
        </p>
        <p>
          <strong>
            By knowing your EV&apos;s energy consumption, you can estimate the
            amount of electricity needed
          </strong>{' '}
          to charge it fully. This information is crucial for determining the
          size and capacity of the solar panel system required to meet your
          charging needs.
          <strong>
            A higher energy consumption rate will mean a larger solar panel
            system.
          </strong>
        </p>
        <H4>Charging Frequency:</H4>
        <p>
          The frequency at which you charge your electric vehicle also plays a
          significant role in determining the solar panel system size. Some EV
          owners charge their vehicles daily, while others charge less
          frequently depending on their driving habits and available charging
          infrastructure.
        </p>
        <p>
          If you charge your EV daily, you&apos;ll need a solar panel system
          capable of generating enough electricity to cover your daily charging
          requirements.{' '}
          <strong>The more you charge the larger the system.</strong> On the
          other hand, if you charge less frequently, you may have more
          flexibility in sizing your solar panel system accordingly.
        </p>
        <p>
          <strong>
            Consider whether you will be primarily relying on solar EV charging
            or if you&apos;ll have access to other charging options.
          </strong>{' '}
          This will help determine the size and capacity of the solar panel
          system needed to accommodate your charging frequency and ensure your
          EV remains adequately powered.
        </p>
        <H4>Sunlight:</H4>
        <p>
          <strong>
            The availability of sunlight at your location is an important factor
            in the effectiveness of solar EV charging.
          </strong>{' '}
          Solar panels rely on sunlight to generate electricity, so the amount
          and quality of sunlight in your area directly impacts the charging
          performance of your system. Regions with sunshine.{' '}
          <strong>
            Those closer to the equator or areas with a high number of sunny
            days per year, are generally more suitable for solar EV charging.
          </strong>{' '}
          These areas can generate more solar energy, allowing for efficient and
          reliable charging.
        </p>
        <p>
          However,{' '}
          <strong>
            even in areas with less sunlight, solar EV charging can still be
            viable.
          </strong>{' '}
          Advances in solar technology, including improved panel efficiency and
          energy storage solutions, have made it possible to generate
          electricity even in lower sunlight conditions.
        </p>
        <p>
          When evaluating solar EV charging for your location, remember to
          consider the average amount of sunlight available throughout the year.
          This information will help you determine the optimal size and
          placement of your solar panels to maximize energy generation and make
          the most of available sunlight.
        </p>
        <div className="p-4 ">
          <table className="border-collapse border border-gray-300">
            <tbody>
              <tr>
                <td className="p-2 font-semibold bg-primary text-white">
                  Energy Consumption
                </td>
                <td className="p-2">
                  The energy consumption of an EV is measured in kilowatt-hours
                  (kWh) per mile or kilometer. It varies depending on the
                  specific EV model and driving style. Understanding your
                  EV&apos;s energy consumption is essential for determining the
                  size and capacity of the required solar panel system.
                </td>
              </tr>
              <tr>
                <td className="p-2 font-semibold bg-primary text-white">
                  Charging Frequency
                </td>
                <td className="p-2">
                  The frequency at which you charge your EV impacts the size of
                  the solar panel system needed. Daily charging requires a
                  larger system to meet the electricity demand, while less
                  frequent charging provides more flexibility in sizing the
                  solar panel system.
                </td>
              </tr>
              <tr>
                <td className="p-2 font-semibold bg-primary text-white">
                  Sunlight
                </td>
                <td className="p-2">
                  The availability and quality of sunlight affect the
                  effectiveness of solar EV charging. Areas with more sunlight,
                  such as regions closer to the equator or with a high number of
                  sunny days per year, are generally more suitable for efficient
                  and reliable solar EV charging.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <H3 className="text-center mt-0">
          Get Personalized Home Solar Quotes Online.
        </H3>
        <H4 className="text-center">
          See what Solar EV Charging looks like for you.
        </H4>
        <div className="rounded-[2rem]  md:px-9 py-8 mt-8 text-gray-900 bg-slate-100 w-auto">
          <RollingLeadForm cid={cid} />
        </div>
        <H4>How much solar power do I need to charge an electric car?</H4>
        <p>
          <strong>An average of 5-7 solar panels</strong> can generate enough
          electricity to power a Tesla or similar EV.{' '}
        </p>
        <H4>How long does it take to charge an EV using solar?</H4>
        <p>7.5 kW - 6 hours to charge from 20 to 80% (* Hyundai Kona 64kWh)</p>
        <p>
          Keep in mind, these times can vary. Weather, battery, type of EV and
          its charger play a role.
        </p>
        <p>
          To learn more about your own car, checkout our{' '}
          <Link href={'/best-electric-vehicles'}>
            electric vehicle buyers guide
          </Link>
        </p>
        <H4>Solar EV Charging At Home</H4>
        <p>
          Installing a solar EV charging system at home has its perks. It offers
          the convenience of charging your electric vehicle without relying
          solely on public charging stations. By having your own charging
          infrastructure, you have the flexibility to charge your EV at any
          time, so it&apos;s always ready for your next adventure. Solar energy
          can be used to charge the EV directly or it can be stored in a battery
          system for later use. Making it a reliable power source.
        </p>
        <p>
          <strong>
            Solar EV charging at home eliminates the uncertainty surrounding
            energy costs.
          </strong>{' '}
          Instead of being subject to fluctuating electricity rates, you can
          enjoy the stability and predictability of solar energy rates,
          resulting in long-term financial benefits.{' '}
        </p>
        <p>
          Solar EV charging is a solution that combines renewable energy and
          electric vehicles, paving the way for a sustainable transportation
          future. By harnessing the power of the sun, we can drive our EVs with
          clean energy and contribute to a greener tomorrow. To explore the cost
          savings and benefits of solar EV charging specific to your car, we
          encourage you to use our{' '}
          <Link href={'/best-electric-vehicles/calculator'}>
            solar ev charging calculator.
          </Link>{' '}
          This tool will provide you with insights into the potential savings
          and environmental impact of integrating solar energy into your EV
          charging setup.{' '}
        </p>
        <H3 className="text-center">
          Get Personalized Home Solar Quotes Online.
        </H3>
        <H4 className="text-center">
          See what Solar EV Charging looks like for you.
        </H4>
        <div className="rounded-[2rem]  md:px-9 py-8 mt-8 text-gray-900 bg-slate-100 w-auto">
          <RollingLeadForm cid={cid} />
        </div>
      </CenteredContentSection>
    </>
  );
};

export default Calculator;
